import React from "react";
import {
  Rating,
  Tooltip,
  SvgIcon,
  useTheme,
  Typography,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import GlassTileLayout from "../layouts/GlassTileLayout";

// Bar height icons showing increasing effort levels
const OneBarIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <rect x="10" y="18" width="4" height="4" fill="currentColor" />
  </SvgIcon>
);

const TwoBarIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <rect x="8" y="18" width="3" height="4" fill="currentColor" />
    <rect x="13" y="15" width="3" height="7" fill="currentColor" />
  </SvgIcon>
);

const ThreeBarIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <rect x="6" y="18" width="3" height="4" fill="currentColor" />
    <rect x="10.5" y="15" width="3" height="7" fill="currentColor" />
    <rect x="15" y="12" width="3" height="10" fill="currentColor" />
  </SvgIcon>
);

const FourBarIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <rect x="5" y="18" width="2.5" height="4" fill="currentColor" />
    <rect x="8.5" y="15" width="2.5" height="7" fill="currentColor" />
    <rect x="12" y="12" width="2.5" height="10" fill="currentColor" />
    <rect x="15.5" y="9" width="2.5" height="13" fill="currentColor" />
  </SvgIcon>
);

const FiveBarIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <rect x="4" y="18" width="2.5" height="4" fill="currentColor" />
    <rect x="7.5" y="15" width="2.5" height="7" fill="currentColor" />
    <rect x="11" y="12" width="2.5" height="10" fill="currentColor" />
    <rect x="14.5" y="9" width="2.5" height="13" fill="currentColor" />
    <rect x="18" y="6" width="2.5" height="16" fill="currentColor" />
  </SvgIcon>
);

// Icons mapping - higher rating = better/easier entry
const effortIcons = {
  1: { icon: FiveBarIcon, label: "Severe" },
  2: { icon: FourBarIcon, label: "Hard" },
  3: { icon: ThreeBarIcon, label: "Difficult" },
  4: { icon: TwoBarIcon, label: "Moderate" },
  5: { icon: OneBarIcon, label: "Easy" },
};

// Descriptions - higher rating = easier/better entry
const effortDescriptions = {
  1: "Very difficult access requiring substantial physical effort, technical terrain, or long trek.",
  2: "Challenging hike with significant elevation, rough terrain, or considerable distance.",
  3: "Moderate hike with some elevation change, uneven terrain, or longer distance.",
  4: "Short walk required, minimal terrain challenges or gear carrying.",
  5: "Direct beach access or very short distance from parking to water.",
};

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconEmpty .MuiSvgIcon-root": {
    color: theme.palette.grey[300],
  },
  "& .MuiRating-iconFilled .MuiSvgIcon-root": {
    color: theme.palette.success.main, // Green for higher rating (more desirable/easier)
  },
}));

const IconContainer = ({ value, ...other }) => {
  const iconData = effortIcons[value];
  return (
    <span {...other}>{iconData ? <iconData.icon /> : <OneBarIcon />}</span>
  );
};

const SlogToWaterTile = ({ slogValue }) => {
  const theme = useTheme();
  console.log("SlogToWaterTile rendering. slogValue:", slogValue);

  const getLabelTextForRating = (value) => {
    const iconData = effortIcons[value];
    return iconData?.label || `Level ${value}`;
  };

  const getTooltipContent = (value) => {
    const label = getLabelTextForRating(value);
    const description =
      effortDescriptions[value] ||
      "Rating indicates the physical effort required to get to the water.";

    const content = (
      <React.Fragment>
        <Typography
          color="inherit"
          variant="subtitle2"
          sx={{ fontWeight: 600 }}
        >
          Entry Effort: {label}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      </React.Fragment>
    );
    console.log(
      "Generated Tooltip content for slogValue",
      value,
      ":",
      description
    );
    return content;
  };

  return (
    <Tooltip
      title={getTooltipContent(slogValue)}
      placement="top"
      arrow
      enterDelay={0}
      leaveDelay={100}
      enterTouchDelay={0}
    >
      <Box
        tabIndex={0}
        role="button"
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          cursor: "pointer",
        }}
      >
        <GlassTileLayout
          icon={effortIcons[slogValue]?.icon || FiveBarIcon}
          label="Entry Effort"
          value={getLabelTextForRating(slogValue)}
        >
          <StyledRating
            name="entry-effort"
            value={typeof slogValue === "number" ? slogValue : 0}
            size="small"
            IconContainerComponent={IconContainer}
            highlightSelectedOnly
            readOnly
            sx={{
              "& .MuiRating-icon": {
                margin: "0 2px",
              },
            }}
          />
        </GlassTileLayout>
      </Box>
    </Tooltip>
  );
};

export default SlogToWaterTile;
